import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import RuleIcon from "@mui/icons-material/Rule";
import { useMemo, useRef, type SyntheticEvent } from "react";
import { FilterList, FilterListItem, FilterLiveForm, SavedQueriesList, useGetList, useListContext } from "react-admin";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { StyledListAside } from "@/components/ui/react-admin/StyledListAside";
import { ResourceName, type ValidAny } from "@/types";
import type { DuplicateClaimantPair } from "@/types/duplicateClaimantPair";

/**
 * Client autocomplete, mirroring ClaimantListAside's ClientFilterInput.
 *
 * Options are narrowed to clients that actually HAVE a pair. Offering a client
 * with nothing to review is a dead end, and on a triage queue the option list
 * should describe the work that exists.
 *
 * Options come from list context — the rows the list has ALREADY fetched — not
 * from a second query. The first version prefetched with `perPage: 1000`, which
 * had two faults (review !4630 thread 27549):
 *
 *   1. The handler caps at 200 (`duplicatePairMaxLimit`), so past 200 pairs the
 *      dropdown silently lost clients — and being ordered `severity_ratio DESC`,
 *      the ones dropped were the LOWEST severity. That is the long tail a client
 *      filter is most useful for: a client whose only pairs are five $0/$0
 *      same-name dependents became unreachable, while the high-severity clients
 *      you would have found anyway stayed. Wrong end to truncate.
 *   2. It carried no `status`, so it offered clients whose pairs were all merged,
 *      dismissed or self-resolved. Selecting one yielded an empty list — exactly
 *      the dead end the paragraph above claims to avoid.
 *
 * List context fixes both by construction: it inherits every filter the list has
 * applied, including the `status: "open"` default, and costs no extra request.
 * The trade is that options are scoped to the loaded page. If that proves too
 * narrow, the honest fix is a `GET /duplicate-claimant-pairs/clients` returning
 * distinct client ids for open pairs — not a larger prefetch, which only moves
 * the truncation point.
 */
const ClientFilterInput = () => {
  const { control } = useFormContext();
  const hasInitialized = useRef(false);

  const { data: pairs } = useListContext<DuplicateClaimantPair & { id: string }>();

  const { data: allClients, isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  const clientIds = useMemo(
    () =>
      new Set(
        (pairs ?? [])
          .map((p) => p.client_id)
          .filter(Boolean)
          .map(String)
      ),
    [pairs]
  );

  const options = useMemo(() => (allClients ?? []).filter((c) => clientIds.has(String(c.id))), [allClients, clientIds]);

  if (!isLoading && options.length > 0) {
    hasInitialized.current = true;
  }

  return (
    <Controller
      name="client_id"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={options}
          getOptionLabel={(option) => option?.client_legal_name || ""}
          value={hasInitialized.current ? (options.find((c) => c.id === field.value) ?? null) : null}
          onChange={(_event: SyntheticEvent, newValue: ValidAny) => {
            field.onChange(newValue?.id || null);
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Client" variant="outlined" />}
          fullWidth
          clearOnEscape
        />
      )}
    />
  );
};

/**
 * Filter sidebar for the duplicate claimant review list (#6717).
 *
 * Mirrors ClaimantListAside so the two surfaces read as one product: same
 * StyledListAside shell, same FilterList/FilterListItem idiom, same
 * SavedQueriesList affordance.
 *
 * Deliberately NO FilterLiveSearch. The backend list endpoint takes
 * status / severity / client_id and has no free-text `q` parameter — rendering a
 * search box that silently does nothing is worse than omitting it. It lands when
 * the endpoint supports it.
 *
 * Filter values are static rather than data-derived (ClaimantListAside derives
 * relationships from the loaded rows). These vocabularies are fixed by DB CHECK
 * constraints, so enumerating them is correct and avoids a 10,000-row prefetch
 * just to discover four severities.
 */
export const DuplicateClaimantPairListAside = () => (
  <StyledListAside>
    <SavedQueriesList />

    <FilterLiveForm>
      <Stack spacing={2}>
        <ClientFilterInput />
      </Stack>
    </FilterLiveForm>

    {/*
      Severity is the primary triage axis — combined spend against the specific
      deductible. High means the pair is close to hiding a breach.
    */}
    <FilterList label="Severity" icon={<WarningAmberIcon />}>
      <FilterListItem label="High" value={{ severity: "high" }} />
      <FilterListItem label="Medium" value={{ severity: "medium" }} />
      <FilterListItem label="Low" value={{ severity: "low" }} />
    </FilterList>

    {/*
      The list defaults to Open. These let a reviewer audit their own past calls.

      NOTE a re-raised pair is `open`, NOT dismissed. The upsert's status CASE
      sets 'open' when dismissed_fingerprint no longer matches the evidence
      fingerprint, and was_re_raised is `status == 'open' && dismissed_at != nil`.
      So a re-raise is never waiting under a facet -- it is already at the top of
      the default Open queue with the Re-raised chip, which is the whole design.
      Dismissed shows the calls that have HELD: pairs whose evidence has not
      moved. Getting this backwards is how someone later "fixes" the Open queue
      by filtering re-raises out of it (review !4630 thread 27550).
    */}
    <FilterList label="Status" icon={<FactCheckIcon />}>
      <FilterListItem label="Open" value={{ status: "open" }} />
      <FilterListItem label="Confirmed" value={{ status: "confirmed" }} />
      <FilterListItem label="Dismissed" value={{ status: "dismissed_not_duplicate" }} />
      {/* Merged is empty until #6519 ships, and kept deliberately -- it populates
          on its own once merge lands rather than needing a change here.

          Self-resolved is NOT offered. A pair reaches that status only when one of
          its claimants is soft-deleted, and every read on this table carries a
          deleted-claimant NOT EXISTS guard, so the predicate removes the row before
          status is consulted. The facet could never return anything (review !4630
          thread 27551). The value stays in the type union because the column
          legitimately holds it. */}
      <FilterListItem label="Merged" value={{ status: "merged" }} />
    </FilterList>

    <FilterList label="Found By" icon={<TravelExploreIcon />}>
      <FilterListItem label="At load" value={{ detection_source: "load_time_guard" }} />
      <FilterListItem label="Sweep" value={{ detection_source: "sweep" }} />
      {/* #7591: selected by an operator in the Claimants list. Its reason is always
          'manual', so the Reason facet does not repeat it. */}
      <FilterListItem label="Manual" value={{ detection_source: "manual" }} />
    </FilterList>

    <FilterList label="Reason" icon={<RuleIcon />}>
      <FilterListItem label="Date of birth differs" value={{ detection_reason: "dob_mismatch" }} />
      <FilterListItem label="Member ID differs" value={{ detection_reason: "member_id_divergent" }} />
      <FilterListItem label="Same name, multiple matches" value={{ detection_reason: "same_name_siblings" }} />
      <FilterListItem label="Subscriber ID differs" value={{ detection_reason: "subscriber_id_divergent" }} />
      <FilterListItem label="Relationship differs" value={{ detection_reason: "relationship_divergent" }} />
      <FilterListItem label="First name + DOB" value={{ detection_reason: "sweep_first_name_dob" }} />
      <FilterListItem label="Last name + DOB" value={{ detection_reason: "sweep_last_name_dob" }} />
    </FilterList>
  </StyledListAside>
);
